import { Component, inject } from '@angular/core';
import { AsyncPipe, CommonModule, DatePipe } from '@angular/common';
import {
  DataDisplayComponent,
  ButtonComponent,
  DataContainerComponent,
  BadgeComponent
} from 'htx-ui-library';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  BehaviorSubject,
  map,
  Observable,
  switchMap,
  combineLatest,
  catchError,
  of,
  take,
  tap
} from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  OrganizationDetails,
  OrganizationComment,
  AdmissionState,
  CommentType
} from '../../../services/organizations/organization.model';
import { OrganizationService } from '../../../services/organizations/organization.service';
import { NotificationService } from '../../../services/form/notification.service';
import { AuthService } from '../../../services/auth/auth.service';
import { NavigationService } from '../../../services/navigation.service';
import { ProtectedContentComponent } from '../../organisms/protected-content/protected-content.component';
import { CommentsComponent, Comment } from '../../organisms/comments/comments.component';
import {
  PdfViewerDialogComponent,
  PdfViewerDialogData
} from '../../organisms/pdf-viewer/pdf-viewer.component';
import { MatDialog } from '@angular/material/dialog';

type BadgeVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

@Component({
  selector: 'app-approval-status-page',
  standalone: true,
  imports: [
    AsyncPipe,
    CommonModule,
    DataDisplayComponent,
    DataContainerComponent,
    TranslocoDirective,
    TranslocoPipe,
    BadgeComponent,
    ButtonComponent,
    ProtectedContentComponent,
    CommentsComponent
  ],
  templateUrl: './approval-status-page.component.html',
  styleUrl: './approval-status-page.component.scss'
})
export class ApprovalStatusPageComponent {
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private organizationService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private authService = inject(AuthService);
  private navigationService = inject(NavigationService);
  private dialog = inject(MatDialog);

  // Only Orga-Admins and Representatives can access this page
  requiredRoles = ['organization_admin', 'organization_representative'];

  private dataRefresh$ = new BehaviorSubject<void>(undefined);
  private commentsRefresh$ = new BehaviorSubject<void>(undefined);

  // Load organization details for the current user's organization
  data$: Observable<OrganizationDetails> = this.dataRefresh$.pipe(
    switchMap(() => this.organizationService.getMyOrganization()),
    catchError((err) => {
      this.notificationService.showError('approvalStatus.loadingError', 5000);
      return of(null as unknown as OrganizationDetails);
    })
  );

  organizationId$ = this.data$.pipe(map((data) => data?.id));

  admissionState$ = this.data$.pipe(map((data) => data?.admissionState));

  // Load public comments (visible to organization members) with pagination
  private readonly commentsPageSize = 5;
  private commentsPage = 0;
  private allLoadedComments: OrganizationComment[] = [];

  private commentsState$ = new BehaviorSubject<{ items: OrganizationComment[]; hasMore: boolean }>({
    items: [],
    hasMore: false
  });

  private commentsLoader$ = combineLatest([this.organizationId$, this.commentsRefresh$])
    .pipe(
      switchMap(([id]) => {
        if (!id) {
          return of(null);
        }
        this.commentsPage = 0;
        this.allLoadedComments = [];
        return this.organizationService.getPublicComments(id, {
          page: 0,
          size: this.commentsPageSize,
          sort: 'date,desc'
        });
      }),
      tap((result) => {
        if (result) {
          this.allLoadedComments = result.content;
          this.commentsState$.next({
            items: this.allLoadedComments,
            hasMore: !result.pagination.last
          });
        }
      }),
      catchError(() => {
        this.commentsState$.next({ items: [], hasMore: false });
        return of(null);
      })
    )
    .subscribe();

  // Transform comments for the CommentsComponent
  commentsForBox$: Observable<Comment[]> = this.commentsState$.pipe(
    map((state) =>
      state.items.map((c) => ({
        id: c.id,
        author: c.author,
        text: c.text,
        date: c.date,
        type: c.type || CommentType.COMMENT
      }))
    )
  );

  hasMoreComments$: Observable<boolean> = this.commentsState$.pipe(map((state) => state.hasMore));

  // Organization details for display
  organisationDetails$ = this.data$.pipe(
    map((dto: OrganizationDetails) => {
      if (!dto) return [];
      return [
        {
          label: this.translocoService.translate('approvalStatus.details.legalName'),
          value: [dto.legalName + ' ' + dto.legalForm]
        },
        {
          label: this.translocoService.translate('approvalStatus.details.shortName'),
          value: [dto.shortName]
        },
        {
          label: this.translocoService.translate('approvalStatus.details.address'),
          value: [
            `${dto.organisationAddress.street} ${dto.organisationAddress.houseNumber}`,
            `${dto.organisationAddress.postalCode} ${dto.organisationAddress.city}`
          ]
        },
        {
          label: this.translocoService.translate('approvalStatus.details.sector'),
          value: [dto.sector]
        },
        {
          label: this.translocoService.translate('approvalStatus.details.website'),
          value: [dto.website]
        }
      ];
    })
  );

  // Representative details for display
  representativeDetails$ = this.data$.pipe(
    map((dto: OrganizationDetails) => {
      if (!dto) return [];
      return [
        {
          label: this.translocoService.translate('approvalStatus.details.representativeName'),
          value: [
            [
              dto.responsiblePerson.title || '',
              dto.responsiblePerson.firstName,
              dto.responsiblePerson.lastName
            ]
              .filter(Boolean)
              .join(' ')
          ]
        },
        {
          label: this.translocoService.translate('approvalStatus.details.email'),
          value: [dto.responsiblePerson.email]
        },
        {
          label: this.translocoService.translate('approvalStatus.details.phone'),
          value: [dto.responsiblePerson.phoneNumber]
        },
        {
          label: this.translocoService.translate('approvalStatus.details.position'),
          value: [dto.responsiblePerson.position]
        }
      ];
    })
  );

  getStatusBadgeVariant(state: AdmissionState | undefined): BadgeVariant {
    switch (state) {
      case AdmissionState.APPROVED:
        return 'success';
      case AdmissionState.REJECTED:
        return 'danger';
      case AdmissionState.PENDING:
        return 'warning';
      default:
        return 'info';
    }
  }

  getStatusTranslationKey(state: AdmissionState | undefined): string {
    switch (state) {
      case AdmissionState.APPROVED:
        return 'approvalStatus.status.approved';
      case AdmissionState.REJECTED:
        return 'approvalStatus.status.rejected';
      case AdmissionState.PENDING:
        return 'approvalStatus.status.pending';
      default:
        return 'approvalStatus.status.unknown';
    }
  }

  openDocument(): void {
    this.organizationId$.pipe(take(1)).subscribe((id) => {
      if (!id) return;
      this.organizationService.getRegistrationDocument(id).subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          this.dialog.open(PdfViewerDialogComponent, {
            data: { url } as PdfViewerDialogData,
            width: '90%',
            height: '90%',
            maxWidth: '1200px'
          });
        },
        error: () => {
          this.notificationService.showError('approvalStatus.documentError', 3000);
        }
      });
    });
  }

  onCommentAdded(text: string): void {
    this.organizationId$.pipe(take(1)).subscribe((id) => {
      if (!id) return;
      this.organizationService.addOrganizationMessage(id, { text }).subscribe({
        next: () => {
          this.commentsRefresh$.next();
          this.notificationService.showSuccess('approvalStatus.messageSent', 3000);
        },
        error: () => {
          this.notificationService.showError('approvalStatus.messageError', 3000);
        }
      });
    });
  }

  goBack(): void {
    void this.router.navigate(['/admin', 'organizations']);
  }

  loadMoreComments(): void {
    this.commentsPage++;
    this.organizationId$
      .pipe(
        take(1),
        switchMap((id) => {
          if (!id) return of(null);
          return this.organizationService.getPublicComments(id, {
            page: this.commentsPage,
            size: this.commentsPageSize,
            sort: 'date,desc'
          });
        }),
        tap((result) => {
          if (result) {
            this.allLoadedComments = [...this.allLoadedComments, ...result.content];
            this.commentsState$.next({
              items: this.allLoadedComments,
              hasMore: !result.pagination.last
            });
          }
        }),
        catchError(() => of(null))
      )
      .subscribe();
  }
}
